import { AuthState, LocalStorageKey } from '@audacy-clients/client-services/core';
import audacyLogger, { LoggerTopic } from '@audacy-clients/client-services/src/AudacyLogger';
import { isAnySafari } from '@audacy-clients/client-services/src/utils/browser';
import { eraseRubiconUserId } from '@audacy-clients/client-services/src/utils/magnite';
import { auth, isLoggedIn, isSoftDeletedState } from '@audacy-clients/core/atoms/auth';
import { type IMarket, locationMarketsState } from '@audacy-clients/core/atoms/location';
import { playbackResumePointsState } from '@audacy-clients/core/atoms/playbackResumePoints';
import { getProfileData } from '@audacy-clients/core/atoms/profile';
import { userMarketIDsAttribute } from '@audacy-clients/core/constants/brazeAttributes';
import { checkIfHasIncompleteProfileData } from '@audacy-clients/core/hooks/useProfile';
import clientServices, {
  getClientServices,
  useClientServices,
} from '@audacy-clients/core/utils/clientServices';
import { getRecoil, refreshRecoil, setRecoil } from '@audacy-clients/core/utils/recoil';
import { isEqual } from 'lodash';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useRecoilValueLoadable } from 'recoil';

import { appInitializationState } from '~/atoms/app';
import { Path } from '~/routes';
import { viewContext } from '~/state/dataEvents';
import Auth, { openAuthWindow } from '~/utils/auth';
import { setBrazeCustomAttribute } from '~/utils/braze';

type IUseAuth = {
  isLoggedIn: boolean;
  logIn: () => Promise<void>;
  logInAnonymous: () => void;
  logOut: () => Promise<void>;
};

type LoginResult = {
  hasIncompleteProfileData: boolean;
  incompleteProfileData?: string[] | null;
};

const useAuth = (): IUseAuth => {
  const isAppInitialized = useRecoilValue(appInitializationState);
  const loggedInLoadable = useRecoilValueLoadable(isLoggedIn); // because async atom, needs Suspence to work
  const { clientServices } = useClientServices();
  const navigate = useNavigate();

  const onLogIn = useCallback(async () => {
    navigate(Path.Loading);
    audacyLogger.info(`[${LoggerTopic.Auth}] state/auth/index.ts: onLogIn called`);
    const { hasIncompleteProfileData, incompleteProfileData } = await logIn();

    if (hasIncompleteProfileData) {
      const isStepOne =
        incompleteProfileData?.includes('firstName') || incompleteProfileData?.includes('lastName');
      const registrationStep = isStepOne ? Path.RegistrationStepOne : Path.RegistrationStepTwo;

      navigate(registrationStep);
    } else {
      navigate(Path.Home);
    }

    const playbacks: Record<string, number> | undefined = clientServices
      .getPersonalizationServices()
      .dataStore.getDataSync(LocalStorageKey.PLAYBACKS);
    playbacks && setRecoil(playbackResumePointsState, playbacks);
  }, [clientServices, navigate]);

  let loggedIn = false;
  audacyLogger.info(
    `[${LoggerTopic.Auth}] state/auth/index.ts: onLogIn: loggedInLoadable.state: ${loggedInLoadable.state}`,
  );
  switch (loggedInLoadable.state) {
    case 'hasValue':
      loggedIn = loggedInLoadable.contents;
      break;
    case 'loading':
      loggedIn = false;
      break;
    case 'hasError':
      loggedIn = false;
      break;
  }

  return {
    // Need one render loop to avoid SSR VS CSR conflicts
    isLoggedIn: !!(isAppInitialized && loggedIn),
    logIn: onLogIn,
    logInAnonymous,
    logOut,
  };
};

export default useAuth;

export const logIn = async (): Promise<LoginResult> => {
  audacyLogger.info(`[${LoggerTopic.Auth}] state/auth/index.ts: logIn called`);

  const loginResult = {
    hasIncompleteProfileData: false,
  } as LoginResult;
  const cs = await getClientServices();
  const url = cs.getPersonalizationServices().authUrl;
  const dataEvent = await Auth.getToken(url);

  if (!dataEvent) {
    await cs.getPersonalizationServices().setAuthState({ state: AuthState.NONE });
    return loginResult;
  }

  eraseRubiconUserId();

  cs.getPersonalizationServices().setIsSoftDeleted(dataEvent.data?.isSoftDeleted);
  await cs.getPersonalizationServices().setAuthState({
    state: AuthState.AUTH,
    data: dataEvent.data,
    isTrusted: dataEvent.isTrusted,
    origin: dataEvent.origin,
  });

  const newAuthState = cs.getPersonalizationServices().getAuthState();
  setRecoil(auth, newAuthState);

  const isSoftDeleted = await cs.getPersonalizationServices().getIsSoftDeleted();
  setRecoil(isSoftDeletedState, isSoftDeleted);

  const markets = await cs.getDataServices().getUserMarkets(undefined, undefined, true);
  setRecoil(locationMarketsState, { markets });

  const storedMarkets = JSON.parse(
    localStorage.getItem('locationMarketsState') || '{ "markets": [] }',
  );
  const marketIDs = markets.map((market) => market.marketId);
  const brazeMarketIDs: string[] = storedMarkets?.markets.map((market: IMarket) => market.marketId);

  if (marketIDs.length && !isEqual(marketIDs, brazeMarketIDs)) {
    setBrazeCustomAttribute({
      attributeKey: userMarketIDsAttribute.attributeKey,
      attributeValue: marketIDs,
    });
  }

  localStorage.setItem('locationMarketsState', JSON.stringify({ markets }));

  loginResult.incompleteProfileData = await clientServices
    .getPersonalizationServices()
    .getIncompleteProfileData();

  loginResult.hasIncompleteProfileData = !!loginResult.incompleteProfileData?.length;

  cs.getAnalyticServices().sendLoginLogout(
    getRecoil(viewContext),
    loginResult.hasIncompleteProfileData,
    dataEvent.data.loginMethod,
    true,
  );

  return loginResult;
};

export const logOut = async (): Promise<void> => {
  let popup: Window | null = null;

  if (isAnySafari(navigator.userAgent)) {
    popup = openAuthWindow('about:blank');
  }

  const cs = await getClientServices();

  try {
    const logoutUrl = await cs.getPersonalizationServices().executeLogout();

    if (logoutUrl) {
      await Auth.clearToken(logoutUrl, popup || undefined);
    } else {
      popup?.close();
    }
  } catch (_e) {
    popup?.close();
  }

  const hasIncompleteProfileData = await checkIfHasIncompleteProfileData();

  cs.getAnalyticServices().sendLoginLogout(getRecoil(viewContext), hasIncompleteProfileData);

  // Whether or not frontchannel (FA) logout succeeds,
  // set the client to a logged out state

  // reset market state
  setRecoil(locationMarketsState, { markets: [] });
  localStorage.removeItem('locationMarketsState');
  localStorage.removeItem(LocalStorageKey.BRAZE_DEVICE_ID);

  eraseRubiconUserId();

  await logInAnonymous();
  window.location.href = Path.Home;

  refreshRecoil(getProfileData);
};

export const logInAnonymous = async (): Promise<void> => {
  const cs = await getClientServices();
  await cs.getPersonalizationServices().setAuthState({ state: AuthState.ANON });
  const userToken = cs
    .getPersonalizationServices()
    .dataStore.getDataSync<string>(LocalStorageKey.USER_TOKEN);
  cs.getPersonalizationServices().dataStore.setDataSync(LocalStorageKey.BRAZE_DEVICE_ID, userToken);
  setRecoil(auth, cs.getPersonalizationServices().getAuthState());
};
